/**
 * The header bell — what needs your action, and what your pipelines have been
 * doing without you.
 *
 * Derived from existing reads and polled every minute (see `useNotifications`):
 * first the validation to-dos — runs you own waiting at a gate, tests assigned to
 * you that can be executed now — then every pipeline run of the last seven days
 * that was news (a failure, or a run that finished unattended), newest first.
 *
 * The badge counts **unread** items, so "Mark all as read" clears it while the
 * work and the log stay; a new gate, assignment or run lights the bell again.
 * Each row also carries a delete,
 * for the ones not worth keeping. Both states are per-viewer, in the browser
 * only — no new backend surface, no controlled record.
 */

import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Divider,
  Group,
  Indicator,
  Menu,
  ScrollArea,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { IconBell, IconCircleCheck, IconTrash } from "@tabler/icons-react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { notificationColor, notificationIcon, notificationWhen } from "./notificationMeta";
import { type AppNotification, useNotifications } from "./useNotifications";

export function NotificationsMenu() {
  const { notifications, unreadCount, isRead, reload, markRead, markAllRead, dismiss } =
    useNotifications();
  const navigate = useNavigate();

  const go = (n: AppNotification) => {
    markRead(n.id);
    navigate(n.to);
  };

  return (
    <Menu
      position="bottom-end"
      width={380}
      shadow="lg"
      radius="md"
      // Refresh on open so a run that finished seconds ago is reflected at once.
      onChange={(opened) => {
        if (opened) reload();
      }}
    >
      <Menu.Target>
        <Tooltip label="Notifications">
          <Indicator
            disabled={unreadCount === 0}
            label={unreadCount > 99 ? "99+" : unreadCount}
            size={16}
            color="red"
            offset={4}
          >
            <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Notifications">
              <IconBell size={18} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown p={0}>
        <Group justify="space-between" px="sm" py="xs" wrap="nowrap">
          <Text fw={600} size="sm">
            Notifications
          </Text>
          {unreadCount > 0 && (
            <Button variant="subtle" size="compact-xs" onClick={markAllRead}>
              Mark all as read
            </Button>
          )}
        </Group>
        <Divider />

        <ScrollArea.Autosize mah={380}>
          {notifications.length === 0 ? (
            <Group gap="sm" p="md" wrap="nowrap">
              <ThemeIcon variant="light" color="green" radius="xl">
                <IconCircleCheck size={18} />
              </ThemeIcon>
              <div>
                <Text size="sm" fw={600}>
                  All caught up
                </Text>
                <Text size="xs" c="dimmed">
                  Nothing is waiting on you, nothing has failed, and nothing ran while you were
                  away.
                </Text>
              </div>
            </Group>
          ) : (
            notifications.map((n) => (
              <Row
                key={n.id}
                n={n}
                read={isRead(n.id)}
                onClick={() => go(n)}
                onDelete={() => dismiss(n.id)}
              />
            ))
          )}
        </ScrollArea.Autosize>

        <Divider />
        <Group justify="center" py={6}>
          <Anchor component={RouterLink} to="/notifications" size="xs">
            See all notifications
          </Anchor>
        </Group>
      </Menu.Dropdown>
    </Menu>
  );
}

function Row({
  n,
  read,
  onClick,
  onDelete,
}: {
  n: AppNotification;
  read: boolean;
  onClick: () => void;
  onDelete: () => void;
}) {
  return (
    <Menu.Item
      onClick={onClick}
      leftSection={
        <ThemeIcon variant="light" color={notificationColor(n)} size="md" radius="xl">
          {notificationIcon(n)}
        </ThemeIcon>
      }
      rightSection={
        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
          {!read && <Box w={8} h={8} style={{ borderRadius: "50%" }} bg="blue.6" />}
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={`Delete notification: ${n.title}`}
            // The row navigates and the menu closes on click; neither should
            // happen when the intent was to throw the row away.
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onDelete();
            }}
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      }
    >
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <Text size="sm" fw={read ? 500 : 700} truncate>
          {n.title}
        </Text>
        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {notificationWhen(n.at)}
        </Text>
      </Group>
      <Text size="xs" c="dimmed" truncate>
        {n.detail}
      </Text>
    </Menu.Item>
  );
}
